import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

import {
  BooleanParam,
  createEnumParam,
  StringParam,
  withDefault,
} from "serialize-query-params";
import { useQueryParam, useQueryParams } from "use-query-params";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { GET_METRICS_EXPLORER_SAVED_VIEWS } from "@gql/metrics-explorer-saved-views";
import { useNorthbeamQuery } from "@hooks/http";
import { type GetMetricsExplorerSavedViews } from "@north-beam/nb-client-side";
import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  MCAttributionWindowDays,
  NBAccountingMode,
  TimeGranularityV2,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { MetricsExplorerProvider } from "./metrics-explorer-provider";
import {
  type MetricsExplorerStateContextDescriptor,
  type SavedView,
  type TileDescriptor,
  type TileFilterQuery,
} from "./types";

const AccountingModeParam = createEnumParam<INBAccountingMode>(
  NBAccountingMode.keyOrder,
);

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);

const TimeGranularityParam = createEnumParam<ITimeGranularityV2>(
  TimeGranularityV2.keyOrder,
);

const DateRangeParam = CustomJsonParam<FixedDateInterval | DateRangeChoiceV3>();

const AdvancedSearchParam = CustomJsonCompressedParam<any | null>();

const TileFilterQueryParam =
  CustomJsonCompressedParam<TileFilterQuery | null>();

const TilesParam = withDefault(
  CustomJsonCompressedParam<TileDescriptor[]>(),
  [],
);

export const MetricsExplorerStateContext =
  createContext<MetricsExplorerStateContextDescriptor | null>(null);

export const MetricsExplorerStateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();

  const [filters, setFilters] = useQueryParams(
    {
      attributionModel: withDefault(
        StringParam,
        user.getDefaultAttributionModel(""),
      ),
      attributionWindow: withDefault(AttributionWindowParam, "1" as const),
      accountingMode: withDefault(AccountingModeParam, "accrual" as const),
      timeGranularity: withDefault(TimeGranularityParam, "daily" as const),
      dateRange: withDefault(DateRangeParam, "last30Days"),
      filterQuery: withDefault(TileFilterQueryParam, null),
      advancedSearch: withDefault(AdvancedSearchParam, null),
    },
    { updateType: "pushIn" },
  );

  const [chartState, setChartState] = useQueryParams(
    {
      alignAxesScale: withDefault(BooleanParam, false),
    },
    { updateType: "pushIn" },
  );

  const [tiles, setTiles] = useQueryParam("tiles", TilesParam, {
    updateType: "pushIn",
    enableBatching: false,
  });

  const [savedViewId, setSavedViewId] = useQueryParam(
    "savedViewId",
    StringParam,
    { updateType: "pushIn" },
  );

  const setAttributionModel = useCallback(
    (value: string) => {
      setFilters({ attributionModel: value });
    },
    [setFilters],
  );

  const setAttributionWindow = useCallback(
    (value: IMCAttributionWindowDays) => {
      setFilters({ attributionWindow: value });
    },
    [setFilters],
  );

  const setAccountingMode = useCallback(
    (value: INBAccountingMode) => {
      setFilters({ accountingMode: value });
    },
    [setFilters],
  );

  const setTimeGranularity = useCallback(
    (value: ITimeGranularityV2) => {
      setFilters({ timeGranularity: value });
    },
    [setFilters],
  );

  const setDateRange = useCallback(
    (value: FixedDateInterval | DateRangeChoiceV3) => {
      setFilters({ dateRange: value });
    },
    [setFilters],
  );

  const setFilterQuery = useCallback(
    (value: TileFilterQuery | null) => {
      setFilters({ filterQuery: value });
    },
    [setFilters],
  );

  const setAdvancedSearch = useCallback(
    (value: any | null) => {
      setFilters({ advancedSearch: value });
    },
    [setFilters],
  );

  const setAlignAxesScale = useCallback(
    (value: boolean) => {
      setChartState({ alignAxesScale: value });
    },
    [setChartState],
  );

  const { data: savedViewsData, loading: isSavedViewsLoading } =
    useNorthbeamQuery<GetMetricsExplorerSavedViews>(
      GET_METRICS_EXPLORER_SAVED_VIEWS,
    );

  const savedViews = useMemo((): SavedView[] => {
    return savedViewsData?.me.metricsExplorerSavedViews ?? [];
  }, [savedViewsData]);

  const selectSavedView = useCallback((savedView: any) => {
    prettyLogEventPage({
      domain: "Metrics Explorer",
      action: "onClick",
      additionalActionInfo: "Select Saved View",
      data: {
        savedView,
      },
    });

    setSavedViewId(savedView.id);
    setFilters(savedView.config.filters);
    setTiles(savedView.config.tiles);
    setChartState(savedView.config.chartState);
  }, []);

  const initialSavedViewIsSet = useRef(false);

  useEffect(() => {
    if (savedViews.length === 0) {
      return;
    }

    if (initialSavedViewIsSet.current) {
      return;
    }

    initialSavedViewIsSet.current = true;

    const savedView = savedViews.find((e) => String(e.id) === savedViewId);

    if (!savedViewId) {
      selectSavedView(savedViews[0]);
    } else if (!savedView) {
      setSavedViewId(String(savedViews[0].id));
    }
  }, [savedViews, savedViewId]);

  const selectedSavedView = useMemo(
    () => savedViews.find((e) => String(e.id) === savedViewId),
    [savedViewId, savedViews],
  );

  const metricsExplorerFilters = useMemo(() => {
    return {
      attributionModel: filters.attributionModel,
      attributionWindow: filters.attributionWindow,
      accountingMode: filters.accountingMode,
      timeGranularity: filters.timeGranularity,
      dateRange: filters.dateRange,
      filterQuery: filters.filterQuery,
      advancedSearch: filters.advancedSearch,
      level: null,
    };
  }, [
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    filters.timeGranularity,
    filters.dateRange,
    filters.filterQuery,
    filters.advancedSearch,
  ]);

  return (
    <MetricsExplorerStateContext.Provider
      value={{
        attributionModel: filters.attributionModel,
        accountingMode: filters.accountingMode,
        attributionWindow: filters.attributionWindow,
        timeGranularity: filters.timeGranularity,
        dateRange: filters.dateRange,
        filterQuery: filters.filterQuery,
        advancedSearch: filters.advancedSearch,
        alignAxesScale: chartState.alignAxesScale,
        tiles,
        savedViews,
        selectedSavedView,
        setAttributionModel,
        setAttributionWindow,
        setAccountingMode,
        setTimeGranularity,
        setDateRange,
        setFilterQuery,
        setAdvancedSearch,
        selectSavedView,
      }}
    >
      <MetricsExplorerProvider
        filters={metricsExplorerFilters}
        chartState={chartState}
        tiles={tiles}
        setTiles={setTiles}
        setAlignAxesScale={setAlignAxesScale}
      >
        {isSavedViewsLoading ? <LoadingSlide /> : children}
      </MetricsExplorerProvider>
    </MetricsExplorerStateContext.Provider>
  );
};
